import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSite } from '../content/SiteContext';

const ContactSection = () => {
  const { content, t } = useSite();
  const section = content.contact;
  const contactInfo = section.info || [];
  const socialMedia = section.social || [];

  const handleEmailClick = () => {
    const subject = encodeURIComponent(t(section.emailSubject));
    const body = encodeURIComponent(t(section.emailBody));
    window.open(`mailto:${section.email}?subject=${subject}&body=${body}`, '_blank');
  };
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return (
    <section id="contacto" className="relative py-20 md:py-32 overflow-hidden">
      {/* Fondo sofisticado con gradientes múltiples */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-gray-900 to-black"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-purple-900/20 via-transparent to-pink-900/20"></div>
      
      {/* Elementos decorativos */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-blue-500/5 rounded-full blur-2xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header mejorado */}
        <div className="text-center mb-16 md:mb-20">
          {/* Subtítulo elegante */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full mb-6">
            <div className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium text-gray-300 tracking-wide uppercase">{t(section.badge)}</span>
          </div>

          {/* Título principal mejorado */}
          <h2 className="mb-6">
            <span className="block text-4xl md:text-5xl lg:text-6xl font-light text-white mb-2 tracking-tight">
              {t(section.title1)}
            </span>
            <span 
              className="block text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 bg-clip-text text-transparent animate-gradient-x"
              style={{
                backgroundSize: '200% 200%',
                animation: 'gradient-x 3s ease infinite'
              }}
            >
              {t(section.title2)}
            </span>
          </h2>

          {/* Descripción mejorada */}
          <div className="max-w-3xl mx-auto">
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-4 font-light">
              {t(section.subtitle)}
            </p>
            <div className="w-24 h-0.5 bg-gradient-to-r from-purple-400 to-pink-400 mx-auto"></div>
          </div>
        </div>

        {/* Tarjetas de contacto mejoradas */}
        <div className={`${isMobile ? 'space-y-8' : 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10'} mb-16 md:mb-20`}>
          {contactInfo.map((info, index) => (
            <div
              key={index}
              className={`${
                isMobile
                  ? 'mx-4 bg-gradient-to-br from-white/8 to-white/3 backdrop-blur-xl border border-white/20 rounded-3xl p-8 shadow-2xl hover:shadow-purple-500/20'
                  : 'bg-gradient-to-br from-white/8 to-white/3 backdrop-blur-xl border border-white/15 rounded-3xl p-8 md:p-10 shadow-xl hover:shadow-purple-500/20'
              } text-center hover:bg-white/8 hover:border-white/25 hover:scale-105 transition-all duration-500 group relative overflow-hidden`}
            >
              {/* Efecto de brillo sutil */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 via-transparent to-pink-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Elemento decorativo */}
              <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-sm"></div>
              {/* Icono mejorado */}
              <div className="relative mb-6">
                <div className={`${isMobile ? 'w-16 h-16' : 'w-14 h-14 md:w-16 md:h-16'} mx-auto mb-2 group-hover:scale-110 transition-transform duration-300 flex items-center justify-center`}>
                  <img 
                    src={info.icon} 
                    alt={t(info.title)}
                    className="w-full h-full object-contain filter brightness-0 invert"
                  />
                </div>
                <div className="w-12 h-0.5 bg-gradient-to-r from-purple-400 to-pink-400 mx-auto opacity-60"></div>
              </div>

              {/* Contenido mejorado */}
              <div className="relative z-10">
                <h3 className={`${isMobile ? 'text-xl font-semibold text-white mb-4' : 'text-lg md:text-xl font-semibold text-white mb-3'} tracking-wide`}>
                  {t(info.title)}
                </h3>

                <p className={`text-gray-200 font-medium mb-2 ${isMobile ? 'text-base' : 'text-sm md:text-base'} leading-relaxed`}>
                  {t(info.content)}
                </p>

                {t(info.subtitle) && (
                  <p className={`text-gray-400 font-light ${isMobile ? 'text-sm' : 'text-xs md:text-sm'} leading-relaxed`}>
                    {t(info.subtitle)}
                  </p>
                )}
              </div>
              {/* Elemento decorativo solo para móvil */}
              {isMobile && (
                <motion.div
                  className="absolute -top-2 -right-2 w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full opacity-70"
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 0.7 }}
                  transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                />
              )}
            </div>
          ))}
        </div>

        {/* Redes Sociales */}
        <div className="text-center">
          <h3
            className="text-xl md:text-2xl font-bold text-white mb-6 md:mb-8"
            style={{
              fontFamily: 'Inter, sans-serif'
            }}
          >
            {t(section.socialTitle1)}{' '}
            <span
              style={{
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                color: 'transparent',
                backgroundImage: 'linear-gradient(to right, #a78bfa, #f472b6)'
              }}
            >
              {t(section.socialTitle2)}
            </span>
          </h3>

          <div className={`${isMobile ? 'space-y-4 px-4' : 'flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4 md:space-x-8'}`}>
            {socialMedia.map((social, index) => (
              <a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${
                  isMobile
                    ? 'flex items-center p-4 bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-lg border border-white/20 rounded-2xl hover:from-purple-500/10 hover:to-pink-500/10 transition-all duration-500 shadow-lg'
                    : 'flex flex-col items-center p-4 md:p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl hover:bg-white/10 transition-all duration-300'
                }`}
                style={!isMobile ? { minWidth: '120px' } : {}}
              >
                <div className={`${isMobile ? 'w-12 h-12 mr-4' : 'w-10 h-10 md:w-12 md:h-12 mb-2 md:mb-3'} flex items-center justify-center`}>
                  <img
                    src={social.icon}
                    alt={social.name}
                    className="w-full h-full object-contain"
                  />
                </div>

                <div className={isMobile ? 'flex-1' : ''}>
                  <h4
                    className={`text-white font-semibold ${isMobile ? 'text-lg mb-1' : 'mb-1 text-sm md:text-base'}`}
                  >
                    {social.name}
                  </h4>
                  <p
                    className={`text-gray-300 ${isMobile ? 'text-sm' : 'text-xs md:text-sm'}`}
                  >
                    {social.label}
                  </p>
                </div>
                {/* Flecha indicadora solo para móvil */}
                {isMobile && (
                  <div
                    className="text-purple-400 text-xl"
                  >
                    →
                  </div>
                )}
              </a>
            ))}
          </div>
        </div>

        {/* Call to Action */}
        <div className={`text-center mt-12 md:mt-16 ${isMobile ? 'px-4' : ''}`}>
          <button
            onClick={handleEmailClick}
            className={`${
              isMobile
                ? 'w-full px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 text-white font-bold rounded-2xl shadow-2xl text-lg border border-purple-400/30'
                : 'px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-pink-500 to-purple-500 text-white font-bold rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 text-sm md:text-base'
            }`}
          >
            <span>
              {isMobile ? `🚀 ${t(section.cta)}` : t(section.cta)}
            </span>

            {/* Efecto de pulso solo para móvil */}
            {isMobile && (
              <div
                className="absolute inset-0 bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl opacity-50"
              />
            )}
          </button>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
